
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, CreditCard, Loader2 } from "lucide-react";
import { fetchStripeProducts, createCheckoutSession } from "@/services/stripeService";
import { toast } from "@/components/ui/use-toast";
import { useAuth } from "@/hooks/useAuth";

interface StripeProduct {
  id: string;
  name: string;
  description?: string;
  price: number | null;
  currency: string | null;
  interval: string | null;
  features?: string[];
}

const SubscriptionPlans: React.FC = () => {
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingPriceId, setProcessingPriceId] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    const loadProducts = async () => {
      setLoading(true);
      
      try {
        // For demo purposes, create sample products if API call fails
        let fetchedProducts = await fetchStripeProducts();
        
        if (!fetchedProducts || fetchedProducts.length === 0) {
          // Fallback to predefined products
          fetchedProducts = [
            {
              id: "prod_S21Y1gwuk83guE", // Premium ID
              name: "Reader Premium",
              description: "Full access to all premium content",
              price: 9.99,
              currency: "USD",
              interval: "month",
              features: [
                "Unlimited article access",
                "Comment on articles",
                "Personalized recommendations",
                "No ads",
                "Early access to new features"
              ]
            },
            {
              id: "prod_S21XDTirIhaWfz", // Reader Basic ID
              name: "Reader Basic",
              description: "Essential access for casual readers",
              price: 4.99,
              currency: "USD",
              interval: "month",
              features: [
                "Access to standard articles",
                "Basic commenting",
                "Personalized feed"
              ]
            },
            {
              id: "prod_S21Yb5G4caixn2", // Journalist Pro ID
              name: "Journalist Pro",
              description: "For professional writers and creators",
              price: 14.99,
              currency: "USD",
              interval: "month",
              features: [
                "Access to standard articles",
                "Basic commenting",
                "Personalized feed"
              ]
            }
          ];
        } else {
          // Add features to products that don't have them
          fetchedProducts = fetchedProducts.map(product => {
            if (!product.features) {
              if (product.name.includes("Premium")) {
                product.features = [
                  "Unlimited article access",
                  "Comment on articles",
                  "Personalized recommendations",
                  "No ads",
                  "Early access to new features"
                ];
              } else if (product.name.includes("Pro")) {
                product.features = [
                  "Access to standard articles",
                  "Basic commenting",
                  "Personalized feed"
                ];
              } else {
                product.features = [
                  "Access to standard articles",
                  "Basic commenting",
                  "Personalized feed"
                ];
              }
            }
            return product;
          });
        }
        
        setProducts(fetchedProducts);
      } catch (error) {
        console.error('Error loading subscription plans:', error);
        toast({
          variant: 'destructive',
          title: 'Failed to load subscription plans',
          description: 'Please try again later'
        });
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, []);

  const handleSubscribe = async (priceId: string) => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to subscribe",
        variant: "destructive"
      });
      return;
    }

    setProcessingPriceId(priceId);
    
    try {
      const checkoutUrl = await createCheckoutSession(priceId);
      if (checkoutUrl) {
        toast({
          title: "Redirecting to checkout",
          description: "You'll be redirected to complete your subscription"
        });
        
        // Fix: Use window.location.href for top-level redirect instead of iframe
        window.top.location.href = checkoutUrl;
      } else {
        throw new Error("Failed to create checkout session");
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast({
        variant: 'destructive',
        title: 'Subscription failed',
        description: 'There was a problem processing your subscription. Please try again.'
      });
    } finally {
      setProcessingPriceId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading subscription plans...</span>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {products.map((product) => (
        <Card key={product.id} className="flex flex-col h-full border-2 hover:border-primary/50 transition-all">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-xl">{product.name}</CardTitle>
                <CardDescription className="mt-1.5">{product.description}</CardDescription>
              </div>
              {product.name.includes("Premium") && (
                <Badge className="bg-directpress-orange text-white">Popular</Badge>
              )}
            </div>
          </CardHeader>
          
          <CardContent className="flex-grow">
            <div className="mb-4">
              <span className="text-3xl font-bold">
                ${product.name.includes("Pro") ? "14.99" : 
                  product.name.includes("Premium") ? "9.99" : "4.99"}
              </span>
              <span className="text-muted-foreground ml-1">/{product.interval}</span>
            </div>
            
            <ul className="space-y-2 mb-6">
              {product.features?.map((feature, index) => (
                <li key={index} className="flex items-center">
                  <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                  <span className="text-sm">{feature}</span>
                </li>
              ))}
            </ul>
          </CardContent>
          
          <CardFooter className="pt-0">
            <Button 
              onClick={() => handleSubscribe(product.id)}
              disabled={processingPriceId === product.id}
              className={`w-full ${product.name.includes("Premium") ? 
                "bg-directpress-orange text-white hover:bg-directpress-orange/90" : 
                ""}`}
            >
              {processingPriceId === product.id ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="mr-2 h-4 w-4" />
                  Subscribe Now
                </>
              )}
            </Button>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
};

export default SubscriptionPlans;
